'use client';

import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';

import { useDocumentApi } from '@/modules/document/hooks/use-document-api';
import { useOpenDocument } from '@/modules/document/hooks/use-open-document';
import { useProjectApi } from '@/modules/project/hooks/use-project-api';
import { useProjectFileApi } from '@/modules/project-file/hooks/use-project-file-api';
import { useSharedDocumentsApi } from '@/modules/shared-document/hooks/use-shared-documents-api';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { Button } from '@/ui-components/button/button';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import type { AlertDataProps, AlertProps } from './types';

export const AlertModal: FunctionComponent = () => {
	const t = useTranslations();
	const { deleteProject, isPending: isPendingProject } = useProjectApi();
	const { deleteProjectDocument, isPending: isPendingProjectDocument } = useDocumentApi();
	const { leaveSharedDocument, isPending: isPendingSharedDocument } = useSharedDocumentsApi();
	const { getSearchParams, removeAllSearchParams } = useNavigation();
	const openDocument = useOpenDocument();
	const { type, id, userName, documentType } = getSearchParams<AlertDataProps>();
	const { deleteProjectAttachment, isPending: isPendingProjectFile } = useProjectFileApi();

	if (!id) return null;

	const modalData = {
		project: {
			title: t('T_DELETE_PROJECT_CONFIRMATION_TITLE'),
			text: t('T_DELETE_PROJECT_CONFIRMATION_TEXT'),
			onClick: () => deleteProject.mutate({ documentId: id }),
		} satisfies AlertProps,
		document: {
			title: t('T_DELETE_PROJECT_DOCUMENT_CONFIRMATION_TITLE'),
			text: t('T_DELETE_PROJECT_DOCUMENT_CONFIRMATION_TEXT'),
			onClick: () => deleteProjectDocument.mutate({ projectDocumentId: id }),
		} satisfies AlertProps,
		projectFile: {
			title: t('T_DELETE_PROJECT_FILE_CONFIRMATION_TITLE'),
			text: t('T_DELETE_PROJECT_FILE_CONFIRMATION_TEXT'),
			onClick: () => deleteProjectAttachment.mutate({ documentId: id }),
		} satisfies AlertProps,
		sharedDocument: {
			title: t('T_LEAVE_SHARED_DOCUMENT'),
			text: t('T_LEAVE_SHARED_DOCUMENT_CONFIRMATION_TEXT'),
			onClick: () => leaveSharedDocument.mutate({ accessDocumentId: id }),
		} satisfies AlertProps,
		projectIsLocked: {
			title: t('T_PROJECT_IS_LOCKED_CONFIRMATION_TITLE'),
			text: userName ? t('T_PROJECT_IS_LOCKED_CONFIRMATION_TEXT', { userName }) : '',
			onClick: () => {
				openDocument({
					documentId: id,
					documentTypeName: documentType,
					accessLevel: 'view',
				});
			},
		},
	};

	const buttonTexts = {
		project: t('T_DELETE'),
		document: t('T_DELETE'),
		projectFile: t('T_DELETE'),
		sharedDocument: t('T_LEAVE'),
		projectIsLocked: t('T_OPEN_PROJECT_VIEW_MODE'),
	};

	const { title, text, onClick } = modalData[type];
	const isPending = isPendingProject || isPendingProjectDocument || isPendingSharedDocument || isPendingProjectFile;
	const onClickHandler = () => {
		onClick();
		removeAllSearchParams();
	};

	return (
		<Modal name='Alert' title={{ text: title, variant: 'h4', align: 'center' }} dismissible>
			<Flexbox direction='col'>
				<Paragraph variant='text-base' className='text-center'>
					{text}
				</Paragraph>
				<Flexbox justify='center' className='gap-5 py-9'>
					<Button variant='ghost-secondary' className='w-44' onClick={removeAllSearchParams} disabled={isPending} isLoading={isPending}>
						{t('T_CANCEL')}
					</Button>

					<Button variant='primary' className='w-44' onClick={onClickHandler} disabled={isPending} isLoading={isPending}>
						{buttonTexts[type]}
					</Button>
				</Flexbox>
			</Flexbox>
		</Modal>
	);
};
